/**
 * PeriodSelector — scope the stats + capacity views to the whole event or a
 * single day. The same Masters event reads very differently on a Tuesday vs the
 * Saturday peak vs the Sunday exodus, so this is the primary lever for showing
 * different statistical outcomes. Writes `statsPeriod` in the planner store.
 */
import { useEventPlannerStore } from '../store/eventPlannerStore'
import { WINDOW_START, WINDOW_END } from '../data/eventWindow'

const DAY_MS = 24 * 60 * 60 * 1000
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function dayLabel(epoch: number): string {
  const d = new Date(epoch)
  return `${DOW[d.getUTCDay()]} ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`
}

/** Event-window day starts (UTC midnights), WINDOW_START is already a midnight. */
function eventDays(): number[] {
  const out: number[] = []
  for (let d = WINDOW_START; d <= WINDOW_END; d += DAY_MS) out.push(d)
  return out
}

export function PeriodSelector() {
  const period = useEventPlannerStore((s) => s.statsPeriod)
  const setStatsPeriod = useEventPlannerStore((s) => s.setStatsPeriod)
  const days = eventDays()

  const isAll = period.start <= WINDOW_START && period.end >= WINDOW_END
  const value = isAll ? 'all' : String(period.start)

  const onChange = (v: string) => {
    if (v === 'all') {
      setStatsPeriod({ start: WINDOW_START, end: WINDOW_END })
    } else {
      const start = Number(v)
      setStatsPeriod({ start, end: start + DAY_MS - 1 })
    }
  }

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-white/15 bg-black/40 px-2.5 py-1.5 text-xs font-medium text-white/90 outline-none focus:border-sky-400/50"
      title="Scope statistics to a period"
    >
      <option value="all">Entire event</option>
      {days.map((d) => (
        <option key={d} value={d}>
          {dayLabel(d)}
        </option>
      ))}
    </select>
  )
}
